import React, { useState, useMemo } from 'react';
import { ShoppingCart, Package, Plus, Minus, FileText, Trash2, ChevronRight, GripVertical, Code2, Bot, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import SidebarHeader from '@/components/ui/SidebarHeader';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Service } from '@/hooks/services/use-services-catalog';
import { CartItem } from '@/hooks/services/use-shopping-cart';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

interface ServicesCatalogSidebarProps {
  services: Service[];
  cartItems: CartItem[];
  cartTotals: {
    itemCount: number;
    subtotal: number;
  };
  onAddToCart: (serviceId: string, quantity: number) => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClearCart: () => void;
  isLoadingServices?: boolean;
  isAddingToCart?: boolean;
  isUpdating?: boolean;
  isRemoving?: boolean;
}

// Draggable Service Card Component
const DraggableServiceCard: React.FC<{
  service: Service;
  isInCart: boolean;
}> = ({ service, isInCart }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `service-${service.id}`,
    data: {
      type: 'service',
      service,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatPrice = (price: number, currency: string = 'AUD') => {
    const formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: isDragging ? 0.5 : 1, x: 0 }}
      className={cn(
        "relative p-3 rounded-lg cursor-grab active:cursor-grabbing",
        "bg-white dark:bg-gray-800",
        "border border-gray-200 dark:border-gray-700",
        "hover:border-purple-300 dark:hover:border-purple-600",
        "transition-all",
        isDragging && "shadow-lg scale-105 z-50",
        isInCart && "opacity-50"
      )}
    >
      {/* Visual Drag Indicator */}
      <div className="absolute top-2 right-2 p-1 pointer-events-none">
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>
      
      {/* Service Name & Price */}
      <div className="pr-8">
        <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
          {service.name}
        </h4>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {service.description}
        </p>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-base font-semibold text-purple-600 dark:text-purple-400">
            {formatPrice(service.base_price, service.currency_code)}
          </span>
          {service.metadata?.originalPrice && (
            <span className="text-xs text-gray-400 line-through">
              {formatPrice(service.metadata.originalPrice, service.currency_code)}
            </span>
          )}
        </div>
        {isInCart && (
          <span className="text-xs text-green-600 dark:text-green-400 mt-1 inline-block">
            Already in cart
          </span>
        )}
      </div>
    </motion.div>
  );
};

export const ServicesCatalogSidebar: React.FC<ServicesCatalogSidebarProps> = ({
  services,
  cartItems,
  cartTotals,
  onAddToCart,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  isLoadingServices = false,
  isAddingToCart = false,
  isUpdating = false,
  isRemoving = false,
}) => {
  const isMobile = useIsMobile();
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('web_design');

  const formatPrice = (price: number, currency: string = 'AUD') => {
    const formatter = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  };

  const handleQuantityChange = (serviceId: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [serviceId]: Math.max(1, (prev[serviceId] || 1) + delta)
    }));
  };

  const handleAddToCart = (serviceId: string) => {
    const quantity = quantities[serviceId] || 1;
    onAddToCart(serviceId, quantity);
    setQuantities(prev => ({ ...prev, [serviceId]: 1 })); // Reset after adding
  };

  const isServiceInCart = (serviceId: string) => {
    return cartItems.some(item => item.service_id === serviceId);
  };

  // Filter services based on selected category
  const filteredServices = useMemo(() => {
    // Map selected category directly to the database values
    const categoryMap = {
      'web_design': 'web_design',
      'ai_automation': 'ai_automation',
      'ai_consulting': 'ai_consulting'
    };
    
    return services.filter(service => 
      service.category === categoryMap[selectedCategory as keyof typeof categoryMap]
    );
  }, [services, selectedCategory]);

  return (
    <MobileSidebarAccordion
      variant="purple"
      persistKey="services-catalog-sidebar"
      defaultExpanded={false}
      className="h-full"
    >
      <SidebarHeader 
        title="Services Catalog"
        icon={Package} 
        variant="purple"
      />
      
      <div className="flex flex-col h-[600px]">
        {/* Category Filter */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2">
          <ToggleGroup 
            type="single" 
            value={selectedCategory} 
            onValueChange={(value) => setSelectedCategory(value || 'web_design')}
            className="w-full justify-center gap-1"
          >
            <ToggleGroupItem 
              value="web_design" 
              aria-label="Web Design"
              className={cn(
                "flex-1 px-3 py-1.5 text-xs font-medium",
                "data-[state=on]:bg-orange-100 data-[state=on]:text-orange-700",
                "dark:data-[state=on]:bg-orange-950 dark:data-[state=on]:text-orange-300",
                "hover:bg-orange-50 dark:hover:bg-orange-950/50"
              )}
            >
              Web Design
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="ai_automation" 
              aria-label="AI Automation"
              className={cn(
                "flex-1 px-3 py-1.5 text-xs font-medium",
                "data-[state=on]:bg-green-100 data-[state=on]:text-green-700",
                "dark:data-[state=on]:bg-green-950 dark:data-[state=on]:text-green-300",
                "hover:bg-green-50 dark:hover:bg-green-950/50"
              )}
            >
              AI Automation
            </ToggleGroupItem>
            <ToggleGroupItem 
              value="ai_consulting" 
              aria-label="AI Consulting"
              className={cn(
                "flex-1 px-3 py-1.5 text-xs font-medium",
                "data-[state=on]:bg-purple-100 data-[state=on]:text-purple-700",
                "dark:data-[state=on]:bg-purple-950 dark:data-[state=on]:text-purple-300",
                "hover:bg-purple-50 dark:hover:bg-purple-950/50"
              )}
            >
              AI Consulting
            </ToggleGroupItem>
          </ToggleGroup>
          
          {/* Drag instruction helper text */}
          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <GripVertical className="h-3 w-3" />
            Drag services to add them to work orders
          </p>
        </div>
        
        {/* Services Catalog */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
            {isLoadingServices ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No services in this category
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredServices.map((service) => (
                  <DraggableServiceCard
                    key={service.id}
                    service={service}
                    isInCart={isServiceInCart(service.id)}
                  />
                ))}
              </div>
            )}
          </div>
      </div>
    </MobileSidebarAccordion>
  );
};